import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Divider,
  SelectChangeEvent,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Business, Person, Email, Phone, LocationOn, CreditCard, Search, CheckCircle, ArrowBack, ArrowForward } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useSubscriptionPlans, BillingType } from '../../hooks/useSubscriptionPlans';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { configService } from '../../services/configService';

const stripePublishableKey = process.env.REACT_APP_JAMAPP_STRIPE_PUBLISHABLE_KEY;

if (!stripePublishableKey) {
  console.error('Missing REACT_APP_JAMAPP_STRIPE_PUBLISHABLE_KEY environment variable');
}

const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : Promise.resolve(null);

// Google Maps libraries
const libraries: ("places")[] = ["places"];

const VenueAdminRegistration: React.FC = () => {
  const [googleApiKey, setGoogleApiKey] = useState<string>('');
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string>('');

  useEffect(() => {
    const initConfig = async () => {
      try {
        await configService.initialize();
        const key = configService.getGoogleApiKey();
        setGoogleApiKey(key);
        console.log('✅ Google API key loaded for Places');
      } catch (error) {
        console.error('Failed to load config:', error);
        setConfigError('Failed to load configuration. Please refresh the page.');
      } finally {
        setConfigLoading(false);
      }
    };

    initConfig();
  }, []);

  if (configLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (configError) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error">{configError}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <VenueAdminRegistrationForm googleApiKey={googleApiKey} />
    </Elements>
  );
};

interface VenueAdminRegistrationFormProps {
  googleApiKey: string;
}

const VenueAdminRegistrationForm: React.FC<VenueAdminRegistrationFormProps> = ({ googleApiKey }) => {
  // Load subscription plans from Firebase
  const { plans: subscriptionPlans, loading: plansLoading, error: plansError } = useSubscriptionPlans();
  
  const [activeStep, setActiveStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [existingAccountDetected, setExistingAccountDetected] = useState<boolean>(false);
  
  // Form state
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [abn, setAbn] = useState<string>('');
  const [liquorLicence, setLiquorLicence] = useState<string>('');
  const [businessType, setBusinessType] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [postcode, setPostcode] = useState<string>('');
  const [planId, setPlanId] = useState<string>('');
  const [billingType, setBillingType] = useState<BillingType>('monthly');
  
  // ABN lookup state
  const [abnLookupLoading, setAbnLookupLoading] = useState<boolean>(false);
  const [abnLookupError, setAbnLookupError] = useState<string>('');
  const [abnLookupSuccess, setAbnLookupSuccess] = useState<boolean>(false);
  const [abnDetails, setAbnDetails] = useState<any>(null);
  
  // Google Places Autocomplete
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  
  const { signUp, signIn, signOut, refreshUserRole } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  // Load Google Maps script
  const { isLoaded: mapsLoaded } = useLoadScript({
    googleMapsApiKey: googleApiKey,
    libraries: libraries,
  });

  // Set default plan once plans are loaded
  useEffect(() => {
    if (subscriptionPlans.length > 0 && !planId) {
      setPlanId(subscriptionPlans[0].planId);
    }
  }, [subscriptionPlans, planId]);

  // Password validation function
  const validatePassword = useCallback((password: string): boolean => {
    if (password.length < 8) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/[0-9]/.test(password)) return false;
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return false;
    return true;
  }, []);

  // Get password error message
  const getPasswordErrorMessage = useCallback((password: string): string => {
    if (password === '') return '';
    
    const errors = [];
    if (password.length < 8) errors.push('at least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('1 uppercase letter');
    if (!/[0-9]/.test(password)) errors.push('1 number');
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) errors.push('1 special character');
    
    if (errors.length > 0) {
      return `Password must contain: ${errors.join(', ')}`;
    }
    return '';
  }, []);

  // ABN validation function
  const validateABN = useCallback((abnValue: string): boolean => {
    const cleanABN = abnValue.replace(/\s/g, '');
    if (!/^\d{11}$/.test(cleanABN)) return false;
    
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    const digits = cleanABN.split('').map(Number);
    digits[0] -= 1;
    const sum = digits.reduce((acc, digit, index) => acc + (digit * weights[index]), 0);
    return sum % 89 === 0;
  }, []);

  // ABN lookup function
  const lookupABN = useCallback(async (abnValue: string) => {
    const cleanABN = abnValue.replace(/\s/g, '');
    
    if (!validateABN(cleanABN)) {
      setAbnLookupError('Invalid ABN format or checksum');
      return;
    }

    setAbnLookupLoading(true);
    setAbnLookupError('');
    setAbnLookupSuccess(false);

    try {
      const lookupABNFunction = httpsCallable(functions, 'lookupABN');
      const result = await lookupABNFunction({ abn: cleanABN });
      const response = result.data as any;
      
      if (!response || !response.success) {
        const errorMsg = response?.error || 'ABN lookup failed';
        throw new Error(errorMsg);
      }

      const businessDetails = response.data;
      setAbnDetails(businessDetails);
      setAbnLookupSuccess(true);

      if (businessDetails.validationOnly) {
        return;
      }
      
      if (!company && businessDetails.name) setCompany(businessDetails.name);
      if (!street && businessDetails.address.street) setStreet(businessDetails.address.street);
      if (!city && businessDetails.address.city) setCity(businessDetails.address.city);
      if (!state && businessDetails.address.state) setState(businessDetails.address.state);
      if (!postcode && businessDetails.address.postcode) setPostcode(businessDetails.address.postcode);

    } catch (error: any) {
      console.error('ABN lookup error:', error);
      let errorMessage = 'Failed to lookup ABN';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code === 'functions/internal') {
        errorMessage = 'ABN lookup service temporarily unavailable';
      }
      
      setAbnLookupError(errorMessage);
    } finally {
      setAbnLookupLoading(false);
    }
  }, [company, street, city, state, postcode, validateABN]);

  // Handle Google Places selection
  const handlePlaceSelect = useCallback(() => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      
      if (!place.address_components) {
        console.log('No address components found');
        return;
      }

      let streetNumber = '';
      let route = '';
      let locality = '';
      let administrativeArea = '';
      let postalCode = '';

      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes('street_number')) streetNumber = component.long_name;
        if (types.includes('route')) route = component.long_name;
        if (types.includes('locality')) locality = component.long_name;
        if (types.includes('administrative_area_level_1')) administrativeArea = component.short_name;
        if (types.includes('postal_code')) postalCode = component.long_name;
      });

      if (streetNumber && route) setStreet(`${streetNumber} ${route}`);
      else if (route) setStreet(route);
      if (locality) setCity(locality);
      if (administrativeArea) setState(administrativeArea);
      if (postalCode) setPostcode(postalCode);
    }
  }, [autocomplete]);

  const handleFirstNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value), []);
  const handleLastNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value), []);
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setExistingAccountDetected(false); // reset when email changes
  }, []);
  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value), []);
  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value), []);
  const handleConfirmPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value), []);
  const handleCompanyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setCompany(e.target.value), []);

  const handleAbnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAbn(value);
    setAbnLookupError('');
    setAbnLookupSuccess(false);
    setAbnDetails(null);
    
    const cleanValue = value.replace(/\s/g, '');
    if (cleanValue.length === 11 && /^\d{11}$/.test(cleanValue)) {
      setTimeout(() => lookupABN(cleanValue), 500);
    }
  }, [lookupABN]);

  const handleLiquorLicenceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setLiquorLicence(e.target.value),
    []
  );
  const handleStreetChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setStreet(e.target.value), []);
  const handleCityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value), []);
  const handlePostcodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setPostcode(e.target.value), []);

  const validateStep = useCallback((step: number): boolean => {
    switch (step) {
      case 0:
        return !!(firstName && lastName && email && phone && password && password === confirmPassword && validatePassword(password));
      case 1:
        return !!(company && abn && businessType && validateABN(abn));
      case 2:
        return !!(street && city && state && postcode && planId);
      case 3:
        return !!paymentMethodId;
      default:
        return true;
    }
  }, [firstName, lastName, email, phone, password, confirmPassword, validatePassword, company, abn, businessType, validateABN, street, city, state, postcode, planId, paymentMethodId]);

  const handlePaymentMethodCreation = useCallback(async () => {
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      setError('Card element not found');
      setLoading(false);
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: `${firstName} ${lastName}`.trim(),
        email,
        phone,
        address: {
          line1: street,
          city,
          state,
          postal_code: postcode,
          country: 'AU',
        },
      },
    });

    if (error) {
      setError(error.message || 'Failed to process payment information');
    } else if (paymentMethod) {
      setPaymentMethodId(paymentMethod.id);
      setActiveStep(prev => prev + 1);
    }

    setLoading(false);
  }, [stripe, elements, firstName, lastName, email, phone, street, city, state, postcode]);

  const handleNext = useCallback(async () => {
    if (activeStep === 3 && !paymentMethodId) {
      await handlePaymentMethodCreation();
      return;
    }

    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
      setError('');
    } else {
      setError('Please fill in all required fields correctly');
    }
  }, [activeStep, validateStep, paymentMethodId, handlePaymentMethodCreation]);

  const handleBack = useCallback(() => {
    setActiveStep(prev => prev - 1);
    setError('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(3)) {
      setError('Please check all fields are filled correctly');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Try to create a new account first
      // If the email already exists (e.g. from the mobile app), sign in instead
      try {
        await signUp(email, password, null, true);
      } catch (signUpError: any) {
        if (
          signUpError.code === 'auth/email-already-in-use' ||
          signUpError.code === 'auth/email-already-exists'
        ) {
          console.log('📱 Existing account detected — signing in with existing credentials');
          setExistingAccountDetected(true);
          try {
            await signIn(email, password);
          } catch (signInError: any) {
            // Wrong password for existing account
            if (
              signInError.code === 'auth/wrong-password' ||
              signInError.code === 'auth/invalid-credential'
            ) {
              throw new Error(
                'This email is already registered (e.g. from the JamApp mobile app). ' +
                'Please use the same password as your existing account, or contact support.'
              );
            }
            throw signInError;
          }
        } else {
          throw signUpError;
        }
      }

      const createVenueAdmin = httpsCallable(functions, 'createVenueAdmin');
      await createVenueAdmin({
        name: `${firstName} ${lastName}`.trim(),
        email,
        phone,
        company,
        abn,
        businessType,
        address: {
          street,
          city,
          state,
          postcode,
          country: 'Australia',
        },
        planId,
        billingType,
        paymentMethodId,
      });

      await new Promise(resolve => setTimeout(resolve, 1000));
      await refreshUserRole();
      setShowSuccessDialog(true);
      
    } catch (error: any) {
      console.error('Registration error:', error);
      await signOut();
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [validateStep, signUp, signIn, signOut, refreshUserRole, email, password, firstName, lastName, phone, company, abn, businessType, street, city, state, postcode, planId, billingType, paymentMethodId]);

  const handleSelectChange = useCallback((event: SelectChangeEvent<string>) => {
    const { name: fieldName, value } = event.target;
    if (fieldName === 'businessType') setBusinessType(value);
    else if (fieldName === 'state') setState(value);
  }, []);

  const handlePlanSelect = useCallback((selectedPlanId: string) => {
    setPlanId(selectedPlanId);
    setBillingType('monthly'); // reset to monthly when switching plans
  }, []);

  const handleBillingTypeChange = useCallback((type: BillingType) => {
    setBillingType(type);
  }, []);

  const steps = ['Venue Admin Details', 'Company Information', 'Address & Plan', 'Payment Information', 'Review & Submit'];

  const businessTypes = [
    'Restaurant', 'Cafe', 'Bar', 'Pub', 'Cinema', 'Nightclub', 'Fast Food', 'Retail Store', 
    'Salon/Spa', 'Gym/Fitness', 'Other'
  ];

  if (plansLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>Loading registration form...</Typography>
        </Box>
      </Container>
    );
  }

  if (plansError) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error">{plansError}</Alert>
        </Box>
      </Container>
    );
  }

  const renderPersonalDetailsStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" gutterBottom>Venue Admin Information</Typography>

      {existingAccountDetected && (
        <Alert severity="info">
          We found an existing JamApp account with this email. Your existing account will be linked to your venue admin profile.
        </Alert>
      )}
      
      {/* Row 1: First name + Last name */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          sx={{ flex: 1, minWidth: 200 }}
          label="First Name"
          value={firstName}
          onChange={handleFirstNameChange}
          required
          InputProps={{ startAdornment: <Person color="action" sx={{ mr: 1 }} /> }}
        />
        <TextField
          sx={{ flex: 1, minWidth: 200 }}
          label="Last Name"
          value={lastName}
          onChange={handleLastNameChange}
          required
          InputProps={{ startAdornment: <Person color="action" sx={{ mr: 1 }} /> }}
        />
      </Box>

      {/* Row 2: Email + Phone */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          sx={{ flex: 1, minWidth: 250 }}
          label="Email Address"
          type="email"
          value={email}
          onChange={handleEmailChange}
          required
          InputProps={{ startAdornment: <Email color="action" sx={{ mr: 1 }} /> }}
        />
        <TextField
          sx={{ flex: 1, minWidth: 200 }}
          label="Phone Number"
          value={phone}
          onChange={handlePhoneChange}
          required
          InputProps={{ startAdornment: <Phone color="action" sx={{ mr: 1 }} /> }}
        />
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          sx={{ flex: 1, minWidth: 250 }}
          label="Password"
          type="password"
          value={password}
          onChange={handlePasswordChange}
          required
          error={password !== '' && !validatePassword(password)}
          helperText={
            existingAccountDetected
              ? 'Enter your existing JamApp password'
              : password !== ''
                ? getPasswordErrorMessage(password) || 'Strong password ✓'
                : 'Minimum 8 characters with 1 uppercase, 1 number, and 1 special character'
          }
        />
        <TextField
          sx={{ flex: 1, minWidth: 250 }}
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          required
          error={confirmPassword !== '' && password !== confirmPassword}
          helperText={confirmPassword !== '' && password !== confirmPassword ? 'Passwords do not match' : ''}
        />
      </Box>
    </Box>
  );

  const renderCompanyInfoStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" gutterBottom>Company Information</Typography>
      
      <Box sx={{ width: '100%' }}>
        <TextField
          fullWidth
          label="Australian Business Number (ABN)"
          value={abn}
          onChange={handleAbnChange}
          required
          inputProps={{ maxLength: 11 }}
          helperText={
            abnLookupError ? abnLookupError : 
            abnLookupSuccess ? "ABN verified ✓" : 
            "11 digit ABN number"
          }
          error={!!abnLookupError || (abn !== '' && !validateABN(abn))}
          InputProps={{
            endAdornment: abnLookupLoading ? (
              <CircularProgress size={20} />
            ) : abnLookupSuccess ? (
              <CheckCircle color="success" />
            ) : abn.length === 11 ? (
              <IconButton size="small" onClick={() => lookupABN(abn)} disabled={abnLookupLoading}>
                <Search />
              </IconButton>
            ) : null,
          }}
        />
        
        {abnDetails && abnLookupSuccess && (
          <Card variant="outlined" sx={{ mt: 2, p: 2, backgroundColor: abnDetails.validationOnly ? 'warning.50' : 'success.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              {abnDetails.validationOnly ? 'ABN Validated:' : 'ABN Details Found:'}
            </Typography>
            {abnDetails.validationOnly ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>ABN:</strong> {abnDetails.abn} ✓ Valid
                </Typography>
                <Alert severity="info" sx={{ mt: 1 }}>
                  ABN is mathematically valid, but business details could not be retrieved. Please enter your business information manually.
                </Alert>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}><strong>Business Name:</strong> {abnDetails.name}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}><strong>Entity Type:</strong> {abnDetails.entityType}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}><strong>GST Status:</strong> {abnDetails.gstStatus}</Typography>
                {abnDetails.address.street && (
                  <Typography variant="body2">
                    <strong>Registered Address:</strong> {abnDetails.address.street}, {abnDetails.address.city}, {abnDetails.address.state} {abnDetails.address.postcode}
                  </Typography>
                )}
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <Chip 
                label={abnDetails.validationOnly ? "ABN Valid" : "Verified"} 
                color={abnDetails.validationOnly ? "warning" : "success"} 
                size="small" 
                icon={<CheckCircle />}
              />
            </Box>
          </Card>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <TextField
          fullWidth
          label="Business / Trading Name"
          value={company}
          onChange={handleCompanyChange}
          required
          InputProps={{ startAdornment: <Business color="action" sx={{ mr: 1 }} /> }}
        />
      </Box>
      
      <FormControl fullWidth required sx={{ maxWidth: 400 }}>
        <InputLabel>Business Type</InputLabel>
        <Select name="businessType" value={businessType} onChange={handleSelectChange} label="Business Type">
          {businessTypes.map((type) => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  const renderAddressPlanStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" gutterBottom>Address Information</Typography>
      
      {mapsLoaded ? (
        <Autocomplete
          onLoad={setAutocomplete}
          onPlaceChanged={handlePlaceSelect}
          options={{ componentRestrictions: { country: 'au' }, types: ['address'] }}
        >
          <TextField
            fullWidth
            label="Street Address"
            value={street}
            onChange={handleStreetChange}
            required
            placeholder="Start typing your address..."
            InputProps={{ startAdornment: <LocationOn color="action" sx={{ mr: 1 }} /> }}
          />
        </Autocomplete>
      ) : (
        <TextField
          fullWidth
          label="Street Address"
          value={street}
          onChange={handleStreetChange}
          required
          InputProps={{ startAdornment: <LocationOn color="action" sx={{ mr: 1 }} /> }}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField sx={{ flex: 2, minWidth: 200 }} label="City/Suburb" value={city} onChange={handleCityChange} required />
        <FormControl sx={{ flex: 1, minWidth: 120 }} required>
          <InputLabel>State</InputLabel>
          <Select name="state" value={state} onChange={handleSelectChange} label="State">
            <MenuItem value="NSW">NSW</MenuItem>
            <MenuItem value="VIC">VIC</MenuItem>
            <MenuItem value="QLD">QLD</MenuItem>
            <MenuItem value="WA">WA</MenuItem>
            <MenuItem value="SA">SA</MenuItem>
            <MenuItem value="TAS">TAS</MenuItem>
            <MenuItem value="ACT">ACT</MenuItem>
            <MenuItem value="NT">NT</MenuItem>
          </Select>
        </FormControl>
        <TextField sx={{ flex: 1, minWidth: 120 }} label="Postcode" value={postcode} onChange={handlePostcodeChange} required inputProps={{ maxLength: 4 }} />
      </Box>
      
      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" gutterBottom>Choose Your Plan</Typography>
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {subscriptionPlans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            selected={planId === plan.planId}
            billingType={planId === plan.planId ? billingType : 'monthly'}
            onSelect={handlePlanSelect}
            onBillingTypeChange={handleBillingTypeChange}
          />
        ))}
      </Box>
    </Box>
  );

  // Shared styles for individual Stripe card input boxes
  const stripeInputSx = {
    p: '14px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    transition: 'border-color 0.2s',
    '&:focus-within': {
      borderColor: '#F24F2F',
      boxShadow: '0 0 0 2px rgba(242, 79, 47, 0.12)',
    },
  };

  const stripeElementStyle = {
    base: {
      fontSize: '16px',
      color: '#000000',
      fontFamily: "'ClashGrotesk', -apple-system, BlinkMacSystemFont, sans-serif",
      '::placeholder': { color: '#9e9e9e' },
    },
    invalid: { color: '#d32f2f', iconColor: '#d32f2f' },
    complete: { color: '#2e7d32', iconColor: '#2e7d32' },
  };

  const renderPaymentStep = () => {
    const selectedPlan = subscriptionPlans.find(p => p.planId === planId);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h6" gutterBottom>Payment Information</Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          {billingType === 'upfront' ? "Your card will be charged the full 12-month amount after your account is approved. You'll have 12 months access before renewal." : "Payments are securely processed by Stripe. JamApp does not store any of your payment information."}
        </Alert>
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Selected Plan</Typography>
            <Typography variant="h5" color="primary.main">
              {selectedPlan?.name}{' '}—{' '}
              {billingType === 'upfront' && selectedPlan?.priceUpfront
                ? `$${(selectedPlan.priceUpfront / 100).toFixed(2)} for 12 months`
                : `$${(selectedPlan?.priceMonthly ?? 0) / 100}/month`}
            </Typography>
            <Typography variant="body2" color="text.secondary">{selectedPlan?.description}</Typography>
            {billingType === 'upfront' && selectedPlan?.priceUpfront && (
              <Chip
                label={`Save $${Math.round((selectedPlan.priceMonthly * 12 - selectedPlan.priceUpfront) / 100)} vs monthly billing`}
                color="success"
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            <CreditCard sx={{ mr: 1, verticalAlign: 'middle' }} />
            Card Information
          </Typography>
          {stripe && elements ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="success.main">✓ Payments Secured by Stripe</Typography>

              {/* Card Number */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                  Card Number
                </Typography>
                <Box sx={stripeInputSx}>
                  <CardNumberElement options={{ style: stripeElementStyle }} />
                </Box>
              </Box>

              {/* Expiry + CVC side by side */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                    Expiry Date
                  </Typography>
                  <Box sx={stripeInputSx}>
                    <CardExpiryElement options={{ style: stripeElementStyle }} />
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                    CVC
                  </Typography>
                  <Box sx={stripeInputSx}>
                    <CardCvcElement options={{ style: stripeElementStyle }} />
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2.5, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
              <Typography color="text.secondary" variant="body2">
                {!stripe ? 'Initializing Stripe...' : 'Preparing payment form...'}
              </Typography>
            </Box>
          )}
          {/* Only show test card hint in development */}
          {stripePublishableKey?.startsWith('pk_test_') && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                🧪 Test mode — use card: <strong>4242 4242 4242 4242</strong> · any future expiry · any CVC
              </Typography>
            </Alert>
          )}
        </Box>
      </Box>
    );
  };

  const renderReviewStep = () => {
    const selectedPlan = subscriptionPlans.find(p => p.planId === planId);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h6" gutterBottom>Review Your Information</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Card variant="outlined" sx={{ flex: 1, minWidth: 250 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Personal Details</Typography>
              <Typography variant="body2">Name: {firstName} {lastName}</Typography>
              <Typography variant="body2">Email: {email}</Typography>
              <Typography variant="body2">Phone: {phone}</Typography>
              {existingAccountDetected && (
                <Chip label="Existing JamApp account" color="info" size="small" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1, minWidth: 250 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Company Details</Typography>
              <Typography variant="body2">Company: {company}</Typography>
              <Typography variant="body2">ABN: {abn}</Typography>
              <Typography variant="body2">Type: {businessType}</Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Card variant="outlined" sx={{ flex: 1, minWidth: 250 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Address</Typography>
              <Typography variant="body2">{street}<br />{city}, {state} {postcode}<br />Australia</Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1, minWidth: 250 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Selected Plan</Typography>
              <Typography variant="body2">{selectedPlan?.name}</Typography>
              <Typography variant="body2">
                {billingType === 'upfront' && selectedPlan?.priceUpfront
                  ? `$${(selectedPlan.priceUpfront / 100).toFixed(2)} / 12 months`
                  : `$${(selectedPlan?.priceMonthly ?? 0) / 100}/month`}
              </Typography>
              <Chip
                label={billingType === 'upfront' ? '12-Month Upfront' : 'Monthly'}
                size="small"
                color={billingType === 'upfront' ? 'success' : 'default'}
                sx={{ mt: 0.5 }}
              />
              <Typography variant="body2" sx={{ mt: 0.5 }}>Up to {selectedPlan?.limits.maxVenues} venues</Typography>
            </CardContent>
          </Card>
        </Box>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Payment Method</Typography>
            <Typography variant="body2">Payment method secured ✓</Typography>
            <Typography variant="caption" color="text.secondary">
              {billingType === 'upfront' && selectedPlan?.priceUpfront
                ? `You will be charged $${(selectedPlan.priceUpfront / 100).toFixed(2)} upfront after account approval (12 months access)`
                : `You will be charged $${(selectedPlan?.priceMonthly ?? 0) / 100}/month from the date your account is approved`}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: return renderPersonalDetailsStep();
      case 1: return renderCompanyInfoStep();
      case 2: return renderAddressPlanStep();
      case 3: return renderPaymentStep();
      case 4: return renderReviewStep();
      default: return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom align="center">Join Our Venue Network</Typography>
            <Typography variant="body1" color="text.secondary" align="center" paragraph>
              Create your venue admin account to start managing your venues and offers
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button disabled={activeStep === 0} onClick={handleBack} startIcon={<ArrowBack />}>Back</Button>
              {activeStep === steps.length - 1 ? (
                <Button variant="contained" onClick={handleSubmit} disabled={loading} endIcon={<CheckCircle />}>
                  {loading ? 'Creating Account...' : 'Complete Registration'}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext} disabled={loading} endIcon={<ArrowForward />}>
                  {activeStep === 3 ? (loading ? 'Processing Payment...' : 'Secure Payment') : 'Next'}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={showSuccessDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 2 } }}>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>Thank you!</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
            One of our administrative team will review your request and reach out shortly on the details you have provided. We thank you for wanting to be part of the JamApp community!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" size="large" onClick={() => { setShowSuccessDialog(false); window.location.href = '/login'; }} sx={{ px: 4 }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

interface PlanCardProps {
  plan: any;
  selected: boolean;
  billingType: BillingType;
  onSelect: (planId: string) => void;
  onBillingTypeChange: (type: BillingType) => void;
}

const PlanCard: React.FC<PlanCardProps> = React.memo(({
  plan,
  selected,
  billingType,
  onSelect,
  onBillingTypeChange,
}) => {
  const handleClick = useCallback(() => { onSelect(plan.planId); }, [plan.planId, onSelect]);
  const hasUpfront = !!plan.priceUpfront;

  // For upfront, show per-month equivalent price
  const displayPrice = billingType === 'upfront' && hasUpfront
    ? plan.priceUpfront / 12
    : plan.priceMonthly;

  const monthlySaving = hasUpfront
    ? Math.round((plan.priceMonthly * 12 - plan.priceUpfront) / 100)
    : 0;

  const percentOff = hasUpfront && plan.priceMonthly > 0
    ? Math.round((1 - plan.priceUpfront / (plan.priceMonthly * 12)) * 100)
    : 0;

  return (
    <Card
      variant={selected ? "outlined" : "elevation"}
      sx={{
        cursor: 'pointer',
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'grey.300',
        position: 'relative',
        flex: 1,
        minWidth: 200,
        maxWidth: 320,
        minHeight: 280,
        pb: plan.isPopular ? 7 : 0,
      }}
      onClick={handleClick}
    >
      {plan.isPopular && (
        <Box sx={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', bgcolor: 'primary.main', color: 'white', px: 3, py: 1, borderRadius: 2, fontSize: '0.875rem', fontWeight: 'bold', zIndex: 1 }}>
          POPULAR
        </Box>
      )}
      <CardContent sx={{ textAlign: 'center', p: 3 }}>
        <Typography variant="h6" gutterBottom>{plan.name}</Typography>

        {/* Billing type toggle — only shown when upfront option exists */}
        {hasUpfront && selected && (
          <Box
            sx={{
              display: 'inline-flex',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mb: 2,
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                px: 2, py: 0.5,
                cursor: 'pointer',
                bgcolor: billingType === 'monthly' ? 'primary.main' : 'transparent',
                color: billingType === 'monthly' ? 'white' : 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 600,
                transition: 'all 0.2s',
              }}
              onClick={() => onBillingTypeChange('monthly')}
            >
              Monthly
            </Box>
            <Box
              sx={{
                px: 2, py: 0.5,
                cursor: 'pointer',
                bgcolor: billingType === 'upfront' ? 'primary.main' : 'transparent',
                color: billingType === 'upfront' ? 'white' : 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 600,
                transition: 'all 0.2s',
              }}
              onClick={() => onBillingTypeChange('upfront')}
            >
              12 Months
            </Box>
          </Box>
        )}

        {/* Price display */}
        <Typography variant="h3" color="primary.main" gutterBottom>
          ${(displayPrice / 100).toFixed(2)}
          <Typography component="span" variant="body2" color="text.secondary">
            /month
          </Typography>
        </Typography>

        {/* Upfront total + % off chip */}
        {billingType === 'upfront' && hasUpfront && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Billed as ${(plan.priceUpfront / 100).toFixed(2)} upfront
            </Typography>
            {percentOff > 0 && (
              <Chip
                label={`${percentOff}% off`}
                size="small"
                sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: '#e8f5e9', color: '#2e7d32' }}
              />
            )}
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" paragraph>{plan.description}</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>Up to {plan.limits.maxVenues} venue{plan.limits.maxVenues > 1 ? 's' : ''}</Typography>
        <Typography variant="body2">{plan.limits.totalOffersPerMonth} offers per month</Typography>
      </CardContent>
    </Card>
  );
});

PlanCard.displayName = 'PlanCard';

export default VenueAdminRegistration;